import { generateWindowFromReelStrips } from '../../helpers';

export type TFf037Modifier = 'collect' | 'multi' | 'boost';
export type TFf037Mode = 'standard' | 'boosted';
export type TFf037Cell = { reel: number; row: number };

export type TFf037CoinDefinition = {
  id: string;
  kind: 'price' | 'fixed-jackpot' | TFf037Modifier;
  baseValue?: number;
  jackpotType?: string;
};

export type TFf037Placement = TFf037Cell & {
  coinId: string;
  modifierValue?: number;
  prizeCoinId?: string;
};

export type TFf037ResponsePlan = { placements: TFf037Placement[] };

export type TFf037Scenario = {
  version?: 1;
  reelIndexes?: number[];
  forceCoinSpinsTrigger?: boolean;
  holdAndWin?: {
    version: 1;
    responsePlans: TFf037ResponsePlan[];
  };
};

export type TFf037Config = {
  betMultiplier?: number;
  betMultiplierBoosted?: number;
  lines?: number[][];
  paytable?: Record<string, number[]>;
  winLevels?: Array<{ name: string; threshold: number }>;
  limits?: Record<string, number>;
  maxWinInCreditsMultiplier?: number;
  reelsSetup: number[];
  baseSpinsReels: string[][];
  boostedSpinsReels: string[][];
  coinFeature: {
    coin: string;
    coinTrigger: number;
    coinTriggerSpinCount: number;
    coins: TFf037CoinDefinition[];
    coinsOnTriggerWeights: number[];
    coinsOnGameWeights: number[];
    modifierConversionChance?: [number, number];
    modifiersAffectHeldCollects?: boolean;
    boostValues: number[];
    multiValues: number[];
  };
};

export type TFf037EditableEntry = {
  reelIndexes: number[];
  forceCoinSpinsTrigger?: boolean;
  scripted: boolean;
  activeModifiers: TFf037Modifier[];
  responsePlans: TFf037ResponsePlan[];
};

export type TFf037EditorState = {
  mode: TFf037Mode;
  entries: TFf037EditableEntry[];
  unsupportedQueue?: TFf037Scenario[];
  unsupportedGameInput?: Record<string, unknown>;
  unsupportedReason?: string;
};

export type TFf037Projection = {
  planIndex: number;
  occupied: TFf037Placement[];
  respinsRemaining: number;
  terminalReason?: 'full-grid' | 'respins-exhausted';
};

const MODIFIERS: TFf037Modifier[] = ['collect', 'multi', 'boost'];

export const clone = <T,>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value));
export const cellKey = (cell: TFf037Cell) => `${cell.reel}:${cell.row}`;
export const cellOrder = (cell: TFf037Cell) => cell.reel * 3 + cell.row;
export const sortPlacements = (placements: TFf037Placement[]) =>
  [...placements].sort((left, right) => cellOrder(left) - cellOrder(right));

export const reelStripsForMode = (config: TFf037Config, mode: TFf037Mode) =>
  mode === 'boosted' ? config.boostedSpinsReels : config.baseSpinsReels;

export const getVisibleWindow = (
  reelIndexes: number[],
  config: TFf037Config,
  mode: TFf037Mode,
): string[][] => generateWindowFromReelStrips({
  reelIndexes,
  reelStrips: reelStripsForMode(config, mode),
  reels: config.reelsSetup.length,
  rows: config.reelsSetup[0],
});

export const getVisibleCoinCells = (window: string[][], config: TFf037Config): TFf037Cell[] =>
  window.flatMap((reel, reelIndex) => reel.flatMap((symbol, row) =>
    symbol === config.coinFeature.coin ? [{ reel: reelIndex, row }] : []));

export const isNaturalTrigger = (
  reelIndexes: number[],
  config: TFf037Config,
  mode: TFf037Mode,
) => getVisibleCoinCells(getVisibleWindow(reelIndexes, config, mode), config).length >=
  config.coinFeature.coinTrigger;

const earliestStopByCoinCount = (strip: string[], rows: number, coin: string) => {
  const byCount = new Map<number, number>();
  strip.forEach((_symbol, stop) => {
    const visibleCoinFlags: number[] = Array.from({ length: rows }, (_unused, row) =>
      strip[(stop + row) % strip.length] === coin ? 1 : 0);
    const count = visibleCoinFlags.reduce((sum, value) => sum + value, 0);
    if (!byCount.has(count)) byCount.set(count, stop);
  });
  return Array.from(byCount.entries()).map(([count, stop]) => ({ count, stop }));
};

export const findReelIndexesForCoinCount = (
  config: TFf037Config,
  mode: TFf037Mode,
  minimumCoins: number,
  maximumCoins = Infinity,
): number[] | undefined => {
  const strips = reelStripsForMode(config, mode);
  let candidates = new Map<number, number[]>([[0, []]]);
  strips.forEach((strip, reel) => {
    const next = new Map<number, number[]>();
    earliestStopByCoinCount(strip, config.reelsSetup[reel], config.coinFeature.coin)
      .forEach(({ count, stop }) => candidates.forEach((stops, total) => {
        const candidate = [...stops, stop];
        const existing = next.get(total + count);
        if (!existing || candidate.join(',') < existing.join(',')) next.set(total + count, candidate);
      }));
    candidates = next;
  });
  return Array.from(candidates.entries())
    .filter(([count]) => count >= minimumCoins && count <= maximumCoins)
    .sort((left, right) => left[0] - right[0] || left[1].join(',').localeCompare(right[1].join(',')))[0]?.[1];
};

export const getEligibleCoinDefinitions = (
  config: TFf037Config,
  activeModifiers: TFf037Modifier[],
  trigger: boolean,
) => [
  ...config.coinFeature.coins.filter((definition, index) => {
    const weight = (trigger ? config.coinFeature.coinsOnTriggerWeights : config.coinFeature.coinsOnGameWeights)[index];
    if (!Number.isSafeInteger(weight) || weight <= 0) return false;
    if (MODIFIERS.includes(definition.kind as TFf037Modifier)) {
      return activeModifiers.includes(definition.kind as TFf037Modifier);
    }
    return true;
  }),
  ...(config.coinFeature.modifierConversionChance
    ? (['boost', 'multi'] as const).filter(kind => activeModifiers.includes(kind) &&
      !config.coinFeature.coins.some(coin => coin.kind === kind)).map(kind => ({ id: kind, kind })) : []),
];

export const placementForDefinition = (
  cell: TFf037Cell & Partial<TFf037Placement>,
  definition: TFf037CoinDefinition,
  config: TFf037Config,
  activeModifiers: TFf037Modifier[],
  trigger = true,
): TFf037Placement => {
  const position = { reel: cell.reel, row: cell.row };
  const prizes = getEligibleCoinDefinitions(config, activeModifiers, trigger)
    .filter(coin => coin.kind === 'price' || coin.kind === 'fixed-jackpot');
  const previous = getEligibleCoinDefinitions(config, activeModifiers, trigger)
    .find(coin => coin.id === cell.coinId);
  const wasModifier = previous?.kind === 'boost' || previous?.kind === 'multi';
  const selectedPrize = wasModifier ? cell.prizeCoinId : cell.coinId;
  const prizeCoinId = config.coinFeature.modifierConversionChance
    ? wasModifier && selectedPrize === undefined
      ? undefined // Keep an explicitly random starting prize when changing modifier.
      : prizes.find(coin => coin.id === selectedPrize)?.id ?? prizes[0]?.id
    : undefined;
  const prize = prizeCoinId ? { prizeCoinId } : {};
  if (definition.kind === 'boost') {
    return { ...position, coinId: definition.id, ...prize, modifierValue: config.coinFeature.boostValues[0] };
  }
  if (definition.kind === 'multi') {
    return { ...position, coinId: definition.id, ...prize, modifierValue: config.coinFeature.multiValues[0] };
  }
  return { ...position, coinId: definition.id };
};

export const defaultPlacement = (
  cell: TFf037Cell,
  config: TFf037Config,
  activeModifiers: TFf037Modifier[],
  trigger: boolean,
) => {
  const definitions = getEligibleCoinDefinitions(config, activeModifiers, trigger);
  const definition = definitions.find(candidate => candidate.kind === 'price') || definitions[0];
  if (!definition) throw new Error('No eligible FF037 Coin definition is configured');
  return placementForDefinition(cell, definition, config, activeModifiers, trigger);
};

const placementIsEligible = (
  placement: TFf037Placement,
  config: TFf037Config,
  activeModifiers: TFf037Modifier[],
  trigger: boolean,
) => {
  if (Object.keys(placement).some(key => !['reel', 'row', 'coinId', 'modifierValue', 'prizeCoinId'].includes(key))) return false;
  if (placement.reel < 0 || placement.reel >= config.reelsSetup.length ||
    placement.row < 0 || placement.row >= config.reelsSetup[placement.reel]) return false;
  const definition = getEligibleCoinDefinitions(config, activeModifiers, trigger)
    .find(candidate => candidate.id === placement.coinId);
  if (!definition) return false;
  if (placement.prizeCoinId !== undefined &&
    (!['boost', 'multi'].includes(definition.kind) || !config.coinFeature.modifierConversionChance ||
      !getEligibleCoinDefinitions(config, activeModifiers, trigger).some(coin =>
        coin.id === placement.prizeCoinId && ['price', 'fixed-jackpot'].includes(coin.kind)))) return false;
  if (definition.kind === 'boost') {
    return typeof placement.modifierValue === 'number' &&
      config.coinFeature.boostValues.includes(placement.modifierValue);
  }
  if (definition.kind === 'multi') {
    return typeof placement.modifierValue === 'number' &&
      config.coinFeature.multiValues.includes(placement.modifierValue);
  }
  return placement.modifierValue === undefined;
};

export const createBlankEntry = (config: TFf037Config): TFf037EditableEntry => ({
  reelIndexes: config.reelsSetup.map(() => 0),
  scripted: false,
  activeModifiers: [...MODIFIERS],
  responsePlans: [],
});

export const createBlankState = (config: TFf037Config): TFf037EditorState => ({
  mode: 'standard',
  entries: [createBlankEntry(config)],
});

export const scriptEntry = (
  entry: TFf037EditableEntry,
  config: TFf037Config,
  mode: TFf037Mode,
): TFf037EditableEntry => {
  entry = { ...entry, activeModifiers: [...MODIFIERS] };
  const triggerCells = getVisibleCoinCells(getVisibleWindow(entry.reelIndexes, config, mode), config);
  const forced = entry.forceCoinSpinsTrigger && triggerCells.length > 0 && triggerCells.length < config.coinFeature.coinTrigger;
  if (!forced && triggerCells.length < config.coinFeature.coinTrigger) {
    return { ...entry, scripted: false, responsePlans: [] };
  }
  const existing = new Map((entry.responsePlans[0]?.placements || []).map(value => [cellKey(value), value]));
  if (forced) {
    const candidates = [...Array.from(existing.values()), ...config.reelsSetup.flatMap((rows, reel) =>
      Array.from({ length: rows }, (_, row) => ({ reel, row })))];
    const occupied = new Set(triggerCells.map(cellKey));
    for (const cell of candidates) {
      if (triggerCells.length >= config.coinFeature.coinTrigger) break;
      if (cell.reel >= config.reelsSetup.length || cell.row >= config.reelsSetup[cell.reel] || occupied.has(cellKey(cell))) continue;
      triggerCells.push({ reel: cell.reel, row: cell.row });
      occupied.add(cellKey(cell));
    }
  }
  const placements = triggerCells.map(cell => {
    const prior = existing.get(cellKey(cell));
    return prior && placementIsEligible(prior, config, entry.activeModifiers, true)
      ? prior
      : defaultPlacement(cell, config, entry.activeModifiers, true);
  });
  if (forced) {
    // A pot entry contains one to three distinct modifier Coins. Keep an initial
    // Collect by default, and prevent repeated modifiers in the scripted entry.
    const seen = new Set<string>();
    placements.forEach((placement, index) => {
      const kind = getEligibleCoinDefinitions(config, entry.activeModifiers, true).find(coin => coin.id === placement.coinId)?.kind;
      if (!MODIFIERS.includes(kind as TFf037Modifier)) return;
      if (seen.has(kind!)) placements[index] = defaultPlacement(placement, config, entry.activeModifiers, true);
      else seen.add(kind!);
    });
    if (seen.size === 0) {
      const collect = config.coinFeature.coins.find(coin => coin.kind === 'collect')!;
      placements[0] = placementForDefinition(placements[0], collect, config, entry.activeModifiers);
    }
  }
  return {
    ...entry,
    scripted: true,
    responsePlans: [{ placements: sortPlacements(placements) }, ...entry.responsePlans.slice(1)],
  };
};

export const projectResponsePlans = (
  entry: TFf037EditableEntry,
  config: TFf037Config,
): TFf037Projection[] => {
  if (!entry.scripted || entry.responsePlans.length === 0) return [];
  const occupied = new Map<string, TFf037Placement>();
  let respinsRemaining = config.coinFeature.coinTriggerSpinCount;
  const projections: TFf037Projection[] = [];
  entry.responsePlans.forEach((plan, planIndex) => {
    plan.placements.forEach(placement => occupied.set(cellKey(placement), clone(placement)));
    if (planIndex > 0) {
      respinsRemaining = plan.placements.length > 0
        ? config.coinFeature.coinTriggerSpinCount
        : Math.max(0, respinsRemaining - 1);
    }
    const terminalReason = occupied.size === config.reelsSetup.reduce((sum, rows) => sum + rows, 0)
      ? 'full-grid' as const
      : planIndex > 0 && respinsRemaining === 0
        ? 'respins-exhausted' as const
        : undefined;
    projections.push({
      planIndex,
      occupied: sortPlacements(Array.from(occupied.values())),
      respinsRemaining,
      ...(terminalReason ? { terminalReason } : {}),
    });
  });
  return projections;
};

export const reconcileEntry = (
  entry: TFf037EditableEntry,
  config: TFf037Config,
  mode: TFf037Mode,
): TFf037EditableEntry => {
  entry = { ...entry, activeModifiers: [...MODIFIERS] };
  if (!entry.scripted) return { ...entry, responsePlans: [] };
  const scripted = scriptEntry(entry, config, mode);
  if (!scripted.scripted) return scripted;
  const occupied = new Set(scripted.responsePlans[0].placements.map(cellKey));
  const plans = [scripted.responsePlans[0]];
  let respinsRemaining = config.coinFeature.coinTriggerSpinCount;
  for (const plan of scripted.responsePlans.slice(1)) {
    if (occupied.size === config.reelsSetup.reduce((sum, rows) => sum + rows, 0)) break;
    const seen = new Set<string>();
    const placements = sortPlacements(plan.placements.filter(placement => {
      const key = cellKey(placement);
      if (occupied.has(key) || seen.has(key) ||
        !placementIsEligible(placement, config, scripted.activeModifiers, false)) return false;
      seen.add(key);
      occupied.add(key);
      return true;
    }));
    plans.push({ placements });
    respinsRemaining = placements.length > 0
      ? config.coinFeature.coinTriggerSpinCount
      : Math.max(0, respinsRemaining - 1);
    if (occupied.size === config.reelsSetup.reduce((sum, rows) => sum + rows, 0) || respinsRemaining === 0) break;
  }
  return { ...scripted, responsePlans: plans.slice(0, 64) };
};

export const addCoinSpin = (
  entry: TFf037EditableEntry,
  config: TFf037Config,
  mode: TFf037Mode,
): TFf037EditableEntry | undefined => {
  if (!entry.scripted) {
    let reelIndexes = entry.reelIndexes;
    if (getVisibleCoinCells(getVisibleWindow(reelIndexes, config, mode), config).length === 0) {
      const stops = findReelIndexesForCoinCount(config, mode, config.coinFeature.coinTrigger) ||
        findReelIndexesForCoinCount(config, mode, 1, config.coinFeature.coinTrigger - 1);
      if (!stops) return undefined;
      reelIndexes = stops;
    }
    entry = scriptEntry({
      ...entry,
      reelIndexes,
      ...(!isNaturalTrigger(reelIndexes, config, mode) ? { forceCoinSpinsTrigger: true } : {}),
    }, config, mode);
  }
  const scripted = reconcileEntry(entry, config, mode);
  const projections = projectResponsePlans(scripted, config);
  if (projections[projections.length - 1]?.terminalReason || scripted.responsePlans.length >= 64) return scripted;
  return reconcileEntry({
    ...scripted,
    responsePlans: [...scripted.responsePlans, { placements: [] }],
  }, config, mode);
};

export const serializeScenarioQueue = (state: TFf037EditorState): TFf037Scenario[] => {
  if (state.unsupportedQueue) return clone(state.unsupportedQueue);
  return state.entries.map(entry => ({
    version: 1,
    reelIndexes: [...entry.reelIndexes],
    ...(entry.forceCoinSpinsTrigger !== undefined ? { forceCoinSpinsTrigger: entry.forceCoinSpinsTrigger } : {}),
    ...(entry.scripted ? {
      holdAndWin: {
        version: 1 as const,
        responsePlans: clone(entry.responsePlans),
      },
    } : {}),
  }));
};

export const serializeGameInput = (state: TFf037EditorState) => {
  if (state.unsupportedQueue) {
    return state.unsupportedGameInput === undefined ? undefined : clone(state.unsupportedGameInput);
  }
  return { boostedSpinsActive: state.mode === 'boosted' };
};

const validCell = (value: unknown) => !!value && typeof value === 'object' &&
  Number.isSafeInteger((value as TFf037Cell).reel) && Number((value as TFf037Cell).reel) >= 0 &&
  Number.isSafeInteger((value as TFf037Cell).row) && Number((value as TFf037Cell).row) >= 0;

const parseEditableEntry = (scenario: unknown, config: TFf037Config, mode: TFf037Mode) => {
  if (!scenario || typeof scenario !== 'object' || Array.isArray(scenario)) return undefined;
  const value = scenario as TFf037Scenario & Record<string, unknown>;
  if (Object.keys(value).some(key => !['version', 'reelIndexes', 'holdAndWin', 'forceCoinSpinsTrigger'].includes(key)) ||
    (value.forceCoinSpinsTrigger !== undefined && typeof value.forceCoinSpinsTrigger !== 'boolean') ||
    (value.version !== undefined && value.version !== 1) ||
    !Array.isArray(value.reelIndexes) || value.reelIndexes.length !== config.reelsSetup.length ||
    value.reelIndexes.some(index => !Number.isSafeInteger(index) || index < 0)) return undefined;
  const entry: TFf037EditableEntry = {
    reelIndexes: [...value.reelIndexes],
    ...(value.forceCoinSpinsTrigger !== undefined ? { forceCoinSpinsTrigger: value.forceCoinSpinsTrigger } : {}),
    scripted: false,
    activeModifiers: [...MODIFIERS],
    responsePlans: [],
  };
  if (value.holdAndWin === undefined) return entry;
  const hold = value.holdAndWin as any;
  if (!hold || typeof hold !== 'object' || Array.isArray(hold) || hold.version !== 1 ||
    Object.keys(hold).some(key => !['version', 'responsePlans'].includes(key)) ||
    !Array.isArray(hold.responsePlans) || hold.responsePlans.length === 0 || hold.responsePlans.length > 64) return undefined;
  const plans: TFf037ResponsePlan[] = [];
  for (const plan of hold.responsePlans) {
    if (!plan || typeof plan !== 'object' || Array.isArray(plan) || Object.keys(plan).some(key => key !== 'placements') ||
      !Array.isArray(plan.placements) || plan.placements.some((placement: unknown) => !validCell(placement) ||
        typeof (placement as TFf037Placement).coinId !== 'string')) return undefined;
    plans.push(clone(plan));
  }
  const parsed = reconcileEntry({
    ...entry,
    scripted: true,
    activeModifiers: [...MODIFIERS],
    responsePlans: plans,
  }, config, mode);
  if (!parsed.scripted || JSON.stringify(parsed.responsePlans) !== JSON.stringify(plans)) return undefined;
  return parsed;
};

export const deserializeScenarioQueue = (
  scenarios: unknown,
  gameInput: unknown,
  config: TFf037Config,
): TFf037EditorState => {
  const input = gameInput && typeof gameInput === 'object' && !Array.isArray(gameInput)
    ? gameInput as Record<string, unknown>
    : undefined;
  const supportedInput = !input || (
    Object.keys(input).every(key => key === 'boostedSpinsActive') &&
    (input.boostedSpinsActive === undefined || typeof input.boostedSpinsActive === 'boolean')
  );
  const mode: TFf037Mode = input?.boostedSpinsActive === true ? 'boosted' : 'standard';
  if (!Array.isArray(scenarios) || scenarios.length === 0 || !supportedInput) {
    return {
      mode,
      entries: [createBlankEntry(config)],
      unsupportedQueue: Array.isArray(scenarios) ? clone(scenarios) : [],
      ...(input ? { unsupportedGameInput: clone(input) } : {}),
      unsupportedReason: 'This preset uses an unsupported FF037 queue or game-input shape.',
    };
  }
  const entries = scenarios.map(scenario => parseEditableEntry(scenario, config, mode));
  if (entries.some(entry => !entry)) {
    return {
      mode,
      entries: [createBlankEntry(config)],
      unsupportedQueue: clone(scenarios),
      ...(input ? { unsupportedGameInput: clone(input) } : {}),
      unsupportedReason: 'This preset cannot be represented safely by the FF037 visual editor.',
    };
  }
  return { mode, entries: entries as TFf037EditableEntry[] };
};

export const getScenarioLabel = (state: TFf037EditorState, config: TFf037Config) => {
  const queue = state.unsupportedQueue ? state.unsupportedQueue : serializeScenarioQueue(state);
  const scripted = state.unsupportedQueue ? 0 : state.entries.filter(entry => entry.scripted).length;
  const plans = state.unsupportedQueue ? 0 : state.entries.reduce((sum, entry) => sum + entry.responsePlans.length, 0);
  return `${state.mode === 'boosted' ? 'Boosted' : 'Standard'} · ${queue.length} play(s) · ${scripted} scripted feature(s) · ${plans} plan(s)`;
};
